import { DEFAULT_LANGUAGE } from '~/modules/config';
import { i18nStore } from '../stores/i18nStore';

export function convertToMoment(dateString: string, language: string = DEFAULT_LANGUAGE): string {
    const msPerMinute = 60 * 1000;
    const msPerHour = msPerMinute * 60;
    const msPerDay = msPerHour * 24;
    const msPerMonth = msPerDay * 30;
    const msPerYear = msPerDay * 365;

    const timestamp = new Date(dateString).getTime();
    const current = Date.now();
    const elapsed = current - timestamp;

    // TODO: Replace text for labels
    const rtf = new Intl.RelativeTimeFormat(language, { numeric: 'always' });
    if (elapsed < msPerMinute) {
        return rtf.format(-Math.floor(elapsed / 1000), 'seconds');
    } else if (elapsed < msPerHour) {
        return rtf.format(-Math.floor(elapsed / msPerMinute), 'minutes');
    } else if (elapsed < msPerDay) {
        return rtf.format(-Math.floor(elapsed / msPerHour), 'hours');
    } else if (elapsed < msPerMonth) {
        return rtf.format(-Math.floor(elapsed / msPerDay), 'days');
    } else if (elapsed < msPerYear) {
        return rtf.format(-Math.floor(elapsed / msPerMonth), 'months');
    }
    // TODO: Review when ago is longer than one year
    return new Date(timestamp).toLocaleDateString();
}

/**
 * Returns a localized string representation of a date in the format DD/MM/YYYY.
 * If the input date string is invalid or empty, returns an empty string.
 */
export function printDateSimple(dateString?: string): string {
    if (!dateString) {
        return '';
    }

    const date = new Date(dateString);
    if (isNaN(date.getTime())) {
        return '';
    }

    const i18n = i18nStore.getState().i18n.shared;
    const day = date.getUTCDate().toString().padStart(2, '0');
    const month = (date.getUTCMonth() + 1).toString().padStart(2, '0');
    const year = date.getUTCFullYear().toString();
    return i18n.dateSimple({ DD: day, MM: month, YYYY: year });
}

/**
 * Calculates the whole number of UTC calendar days since the user's signup date.
 * Returns 0 for a missing or unparseable date AND for a signup earlier today, so a
 * 0 result does NOT by itself mean the date is invalid — use isValidSignupDate for
 * that. Future dates return a negative number.
 */
export function getDaysSinceSignup(signupDate?: string): number {
    if (!signupDate) {
        return 0;
    }

    const signup = new Date(signupDate);
    if (isNaN(signup.getTime())) {
        return 0;
    }

    const now = new Date();

    // Normalize both dates to UTC midnight to compare calendar days
    const signupUTC = Date.UTC(signup.getUTCFullYear(), signup.getUTCMonth(), signup.getUTCDate());
    const nowUTC = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());

    const diffTime = nowUTC - signupUTC;
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
}

/**
 * Returns true only when signupDate is present AND parseable.
 * Used to distinguish a genuinely bad/missing signup date (fail open)
 * from a valid same-day signup (0 days old, which must be gated).
 */
export function isValidSignupDate(signupDate?: string): boolean {
    return !!signupDate && !isNaN(new Date(signupDate).getTime());
}
